import httpClient from '@utils/httpClient';
import useVersionStore from '@/store/VersionStore';
import packageJson from '../../package.json';

/** Baked into the bundle at build time — this is the version we are RUNNING. */
const APP_VERSION = packageJson.version;

const VERSION_CHECK_INTERVAL = 5 * 60 * 1000;

/**
 * The version we last reloaded in order to pick up. If we come back from that
 * reload and the server STILL says we're stale for the same version, the reload
 * isn't reaching a fresh build (a cached index.html, or CDN lag) and reloading
 * again would loop forever. sessionStorage, not localStorage: the guard is about
 * this tab's reload, and a new tab deserves a fresh attempt.
 */
const RELOADED_FOR_KEY = 'version_reloaded_for';

/** setInterval is process-wide; React 18 StrictMode double-invokes effects. */
let monitoring = false;

export const getCurrentVersion = () => APP_VERSION;

/**
 * Get the latest version from server
 */
export const getLatestVersion = async () => {
  try {
    const response = await httpClient.get('/app-version/latest');

    if (response.success && response.data) {
      return {
        version: response.data.version,
        releaseDate: response.data.releaseDate
      };
    }

    return null;
  } catch (error) {
    console.error('Failed to fetch latest version:', error);
    return null;
  }
};

/**
 * Ask the server whether the build we're running is out of date.
 * Fails safe: any error means "assume we're current" rather than triggering a
 * reload we can't justify.
 */
export const checkVersionStatus = async () => {
  const unchanged = {
    currentVersion: APP_VERSION,
    latestVersion: APP_VERSION,
    needsUpdate: false
  };

  try {
    const response = await httpClient.get(`/app-version/check?version=${APP_VERSION}`);

    if (response.success && response.data) {
      return {
        currentVersion: response.data.currentVersion,
        latestVersion: response.data.latestVersion,
        needsUpdate: response.data.needsUpdate
      };
    }

    return unchanged;
  } catch (error) {
    console.error('Failed to check version status:', error);
    return unchanged;
  }
};

/**
 * Reload onto the new build.
 *
 * This does NOT touch localStorage. The previous implementation called
 * localStorage.clear() and then restored 'token' / 'userRole' / 'userId' — none of
 * which are keys this app actually uses (they are 'authToken', 'refreshToken' and
 * 'user'), so every auto-update silently signed the user out. Clearing app state
 * was never necessary anyway: what makes a build stale is the HTTP cache on
 * index.html, and that is fixed by cache headers (see firebase.json), not by
 * deleting the session.
 */
export const performHardRefresh = (latestVersion?: string) => {
  // Remember what we reloaded for, so a reload that fails to land a fresh build
  // can be detected instead of repeated.
  if (latestVersion) {
    try {
      sessionStorage.setItem(RELOADED_FOR_KEY, latestVersion);
    } catch {
      // Private mode / storage disabled. Worst case we lose the loop guard.
    }
  }

  // No service worker ships today, so this is a no-op — but it costs nothing and
  // is the right thing to do the day one does.
  if ('caches' in window) {
    caches.keys().then((names) => names.forEach((name) => caches.delete(name)));
  }

  window.location.reload();
};

/**
 * Did we already reload once for this exact version and come back still stale?
 */
const alreadyReloadedFor = (version: string): boolean => {
  try {
    return sessionStorage.getItem(RELOADED_FOR_KEY) === version;
  } catch {
    return false;
  }
};

const performVersionCheck = async () => {
  const status = await checkVersionStatus();
  if (!status.needsUpdate) return;

  const { latestVersion } = status;

  // The loop guard. If reloading for this version already failed to get us onto
  // it, the new build isn't reachable from here (stale index.html, CDN still
  // catching up). Reloading again would spin forever and lock the user out of the
  // app entirely — so hand them a button and stop.
  const blocked = alreadyReloadedFor(latestVersion);

  if (blocked) {
    console.warn(
      `Already reloaded for v${latestVersion} but still running v${APP_VERSION}. ` +
        'Not auto-reloading again — the new build is not being served to this browser.'
    );
  }

  // The banner owns the countdown and the reload; this service only reports.
  useVersionStore.getState().announceUpdate(latestVersion, blocked);
};

/**
 * Poll for updates, and check again whenever the tab is brought back to the
 * foreground — a tab left open overnight is the most common way to end up on a
 * stale build, and background timers are throttled hard by browsers anyway.
 */
export const startVersionMonitoring = () => {
  if (monitoring) return;
  monitoring = true;

  performVersionCheck();

  setInterval(() => {
    if (document.visibilityState === 'visible') performVersionCheck();
  }, VERSION_CHECK_INTERVAL);

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') performVersionCheck();
  });
};

/**
 * Initialize version management. Call once, at app startup.
 */
export const initializeVersionManagement = () => {
  console.log(`App version: ${APP_VERSION}`);
  window.setTimeout(startVersionMonitoring, 5000);
};

export default {
  getLatestVersion,
  checkVersionStatus,
  performHardRefresh,
  startVersionMonitoring,
  getCurrentVersion,
  initializeVersionManagement
};
